import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Banner, BlockStack, Button, Card, Checkbox, DataTable, Divider, InlineStack, Tabs, Text, TextField } from "@shopify/polaris";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminFitmentImportHistoryItem, AdminShopRuntime, AdminStoreDetails, AdminStoreGeneralSettings, AdminStoreNote } from "../../app/models/Admin";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};


export default function AdminStoreDetailsPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const { shopId } = useParams();
  const [details, setDetails] = useState<AdminStoreDetails | null>(null);
  const [runtime, setRuntime] = useState<AdminShopRuntime | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingRuntime, setLoadingRuntime] = useState(false);
  const [savingActiveState, setSavingActiveState] = useState(false);
  const [deletingWebhookId, setDeletingWebhookId] = useState<string | null>(null);
  const [deletingAllWebhooks, setDeletingAllWebhooks] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const [editedSettings, setEditedSettings] = useState<Record<string, string>>({});
  const [savingSettingKey, setSavingSettingKey] = useState<string | null>(null);
  const [generalSettings, setGeneralSettings] = useState<AdminStoreGeneralSettings>({
    searchEnablePersistentFiltering: false,
    useYmmPrefixForProductTags: true,
  });
  const [savingGeneralSettings, setSavingGeneralSettings] = useState(false);
  const [clearingSurgeMode, setClearingSurgeMode] = useState(false);
  const [downloadingImportQueueId, setDownloadingImportQueueId] = useState<number | null>(null);
  const [deletingImportQueueId, setDeletingImportQueueId] = useState<number | null>(null);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const parsedShopId = Number(shopId);

  const sortedSettings = useMemo(() => {
    if (!details) return [];
    return Object.entries(details.store.syncSettings).sort(([left], [right]) => left.localeCompare(right));
  }, [details]);

  const loadDetails = useCallback(async () => {
    if (!Number.isFinite(parsedShopId)) {
      setError("Invalid store id.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await agent.Admin.storeDetails(token, parsedShopId);
      setDetails(response);
      setEditedSettings(response.store.syncSettings || {});
      setGeneralSettings(response.generalSettings || {
        searchEnablePersistentFiltering: false,
        useYmmPrefixForProductTags: true,
      });
      setLastLoadedAt(new Date());
    } catch {
      setError("Unable to load store details.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [parsedShopId, token, onUnauthorized]);

  const loadRuntime = useCallback(async (silent = false) => {
    if (!Number.isFinite(parsedShopId)) return;

    if (!silent) setLoadingRuntime(true);

    try {
      const response = await agent.Admin.shopRuntime(token, parsedShopId);
      setRuntime(response);
      if (!silent) setLastLoadedAt(new Date());
    } catch {
      if (!silent) setError("Unable to load Shopify runtime status.");
    } finally {
      if (!silent) setLoadingRuntime(false);
    }
  }, [parsedShopId, token]);

  useEffect(() => {
    loadDetails();
    loadRuntime();
  }, [loadDetails, loadRuntime]);

  useEffect(() => {
    if (!Number.isFinite(parsedShopId)) return;
    const id = window.setInterval(() => {
      loadRuntime(true);
    }, 10000);

    return () => window.clearInterval(id);
  }, [parsedShopId, loadRuntime]);

  const handleIsActiveChange = async (newValue: boolean) => {
    if (!details) return;

    setSavingActiveState(true);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreActiveStatus(token, details.store.shopId, newValue);
      setDetails((current) => current ? { ...current, store: { ...current.store, isActive: updated.isActive, updatedAt: updated.updatedAt } } : current);
    } catch {
      setError("Unable to update active status.");
    } finally {
      setSavingActiveState(false);
    }
  };

  const handleSettingEdit = (key: string, value: string) => {
    setEditedSettings((current) => ({ ...current, [key]: value }));
  };

  const handleSettingSave = async (key: string) => {
    if (!details) return;

    const value = editedSettings[key] ?? "";
    setSavingSettingKey(key);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreSyncSetting(token, details.store.shopId, key, value);
      setDetails((current) => current ? { ...current, store: { ...current.store, syncSettings: updated.syncSettings, updatedAt: updated.updatedAt } } : current);
      setEditedSettings(updated.syncSettings || {});
    } catch {
      setError(`Unable to update setting: ${key}.`);
    } finally {
      setSavingSettingKey(null);
    }
  };


  const handleGeneralSettingChange = (key: keyof AdminStoreGeneralSettings, value: boolean) => {
    setGeneralSettings((current) => ({ ...current, [key]: value }));
  };

  const handleGeneralSettingsSave = async () => {
    if (!details) return;

    setSavingGeneralSettings(true);
    setError(null);

    try {
      const updated = await agent.Admin.updateStoreGeneralSettings(token, details.store.shopId, generalSettings);
      setGeneralSettings(updated);
      setDetails((current) => current ? { ...current, generalSettings: updated } : current);
    } catch {
      setError("Unable to update general store settings.");
    } finally {
      setSavingGeneralSettings(false);
    }
  };

  const handleClearSurgeMode = async () => {
    if (!details) return;

    const confirmed = window.confirm("Clear webhook surge mode and reset surge counters for this store?");
    if (!confirmed) return;

    setClearingSurgeMode(true);
    setError(null);

    try {
      const updated = await agent.Admin.clearStoreSurgeMode(token, details.store.shopId);
      setDetails((current) => current ? { ...current, store: { ...current.store, syncSettings: updated.syncSettings, updatedAt: updated.updatedAt } } : current);
      setEditedSettings(updated.syncSettings || {});
    } catch {
      setError("Unable to clear surge mode.");
    } finally {
      setClearingSurgeMode(false);
    }
  };


  const handleDeleteWebhook = async (id: string) => {
    if (!Number.isFinite(parsedShopId)) return;

    const confirmed = window.confirm(`Delete webhook subscription ${id}?`);
    if (!confirmed) return;

    setDeletingWebhookId(id);
    setError(null);

    try {
      await agent.Admin.deleteWebhookSubscription(token, parsedShopId, id);
      await loadRuntime();
    } catch {
      setError("Unable to delete webhook subscription.");
    } finally {
      setDeletingWebhookId(null);
    }
  };

  const handleDeleteAllWebhooks = async () => {
    if (!Number.isFinite(parsedShopId) || !runtime || runtime.webhookSubscriptions.length === 0) return;

    const confirmed = window.confirm(`Delete all ${runtime.webhookSubscriptions.length} webhook subscriptions?`);
    if (!confirmed) return;

    setDeletingAllWebhooks(true);
    setError(null);

    try {
      for (const subscription of runtime.webhookSubscriptions) {
        await agent.Admin.deleteWebhookSubscription(token, parsedShopId, subscription.id);
      }

      await loadRuntime();
    } catch {
      setError("Unable to delete all webhook subscriptions.");
    } finally {
      setDeletingAllWebhooks(false);
    }
  };



  const handleDownloadFitmentImport = async (item: AdminFitmentImportHistoryItem) => {
    if (!details) return;

    setDownloadingImportQueueId(item.queueId);
    setError(null);

    try {
      const response = await agent.Admin.downloadStoreFitmentImportFile(token, details.store.shopId, item.queueId);
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = item.originalFileName || `fitment-import-${item.queueId}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      setError("Unable to download fitment import file.");
    } finally {
      setDownloadingImportQueueId(null);
    }
  };

  const handleAddNote = async () => {
    if (!details) return;

    const trimmedNote = newNote.trim();
    if (!trimmedNote) {
      setError("Note cannot be empty.");
      return;
    }

    setSavingNote(true);
    setError(null);

    try {
      const added = await agent.Admin.addStoreNote(token, details.store.shopId, trimmedNote);
      setDetails((current) => current
        ? {
            ...current,
            notes: [added, ...(current.notes || [])],
          }
        : current);
      setNewNote("");
    } catch {
      setError("Unable to save note.");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteFitmentImport = async (item: AdminFitmentImportHistoryItem) => {
    if (!details) return;

    const confirmed = window.confirm(`Delete fitment import history item ${item.queueId}?`);
    if (!confirmed) return;

    setDeletingImportQueueId(item.queueId);
    setError(null);

    try {
      await agent.Admin.deleteStoreFitmentImportHistoryItem(token, details.store.shopId, item.queueId);
      setDetails((current) => current
        ? {
            ...current,
            fitmentImportHistory: current.fitmentImportHistory.filter((historyItem) => historyItem.queueId !== item.queueId),
          }
        : current);
    } catch {
      setError("Unable to delete fitment import history item.");
    } finally {
      setDeletingImportQueueId(null);
    }
  };

  const forceRefresh = async () => {
    await Promise.all([loadDetails(), loadRuntime()]);
  };

  const tabs = [
    { id: "overview", content: "Overview", accessibilityLabel: "Overview" },
    { id: "runtime", content: "Shopify runtime", accessibilityLabel: "Shopify runtime" },
    { id: "settings", content: "Sync settings", accessibilityLabel: "Sync settings" },
    { id: "notes", content: "Notes", accessibilityLabel: "Notes" },
    { id: "fitment-history", content: "Fitment history", accessibilityLabel: "Fitment history" },
  ];

  const endpointText = (sub: AdminShopRuntime["webhookSubscriptions"][number]) => {
    if (sub.callbackUrl) return sub.callbackUrl;
    if (sub.arn) return sub.arn;
    if (sub.pubSubProject || sub.pubSubTopic) return `${sub.pubSubProject || "-"} / ${sub.pubSubTopic || "-"}`;
    return "-";
  };

  const alerts = useMemo(() => {
    if (!details) return [];

    const parseSettingBool = (key: string) => {
      const rawValue = details.store.syncSettings[key];
      if (!rawValue) return false;

      const normalized = rawValue.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "yes";
    };

    const nextAlerts: Array<{ title: string; tone: "warning" | "critical" | "info"; body: string }> = [];

    if (parseSettingBool("Shopify.WebhookSurgeModeActive")) {
      nextAlerts.push({
        title: "Webhook surge mode is active",
        tone: "critical",
        body: "Incoming webhook processing may be intentionally skipped while surge mode remains enabled.",
      });
    }

    if (!parseSettingBool("Shopify.EnableWebhooks")) {
      nextAlerts.push({
        title: "Webhook processing is disabled",
        tone: "warning",
        body: "This store is configured with Shopify webhooks disabled, so near real-time product updates will not run.",
      });
    }

    if (parseSettingBool("Shopify.AccessTokenAuthorizationHold")) {
      nextAlerts.push({
        title: "Shop access token is on authorization hold",
        tone: "critical",
        body: "The store token is currently held; Shopify API operations may be blocked until authorization is restored.",
      });
    }

    if (parseSettingBool("Shopify.RequestFullProductSync")) {
      nextAlerts.push({
        title: "Full product sync requested",
        tone: "info",
        body: "A full product sync has been requested and may increase processing load until completed.",
      });
    }

    if (runtime?.currentBulkOperation?.errorCode) {
      nextAlerts.push({
        title: "Current bulk operation has an error",
        tone: "critical",
        body: `Shopify reports error code: ${runtime.currentBulkOperation.errorCode}.`,
      });
    }

    return nextAlerts;
  }, [details, runtime]);

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <Button variant="plain" onClick={() => navigate("/pf-console/stores")}>← Back to stores</Button>
        <InlineStack gap="200">
          <Button onClick={loadDetails} loading={loading}>Refresh details</Button>
          <Button onClick={() => loadRuntime()} loading={loadingRuntime}>Refresh runtime</Button>
          <Button variant="primary" onClick={forceRefresh} loading={loading || loadingRuntime}>Force refresh (bypass stale view)</Button>
        </InlineStack>
      </InlineStack>

      {error ? <Text as="p" tone="critical">{error}</Text> : null}
      <Text as="p" tone="subdued">Store details are loaded into the page state; use refresh buttons to reload latest DB/runtime values. Last refreshed: {lastLoadedAt ? lastLoadedAt.toLocaleString() : "not yet"}.</Text>

      {loading ? (
        <Card><Text as="p">Loading store details...</Text></Card>
      ) : !details ? (
        <Card><Text as="p">No details found.</Text></Card>
      ) : (
        <>
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingLg">{details.store.name || details.store.domain || `Shop ${details.store.shopId}`}</Text>
              <InlineStack gap="200">
                <Badge tone={details.store.isActive ? "success" : "warning"}>{details.store.isActive ? "Active" : "Inactive"}</Badge>
                <Text as="span" tone="subdued">{details.store.domain || details.store.myShopifyDomain || "-"}</Text>
              </InlineStack>
              {alerts.map((alert) => (
                <Banner key={alert.title} title={alert.title} tone={alert.tone}>
                  <p>{alert.body}</p>
                </Banner>
              ))}
            </BlockStack>
          </Card>

          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
            <Divider />
            <div style={{ paddingTop: "0.75rem" }}>
              {selectedTab === 0 ? (
                <BlockStack gap="400">
                  <DataTable
                    columnContentTypes={["text", "text"]}
                    headings={["Field", "Value"]}
                    rows={[
                      ["Domain", details.store.domain || details.store.myShopifyDomain || "-"],
                      ["Subscription", `${details.store.subscription?.name || "-"} (${details.store.subscription?.status || "no status"})`],
                      ["Plan", `${details.store.planDisplayName || "-"} | Dev: ${String(details.store.planPartnerDevelopment ?? false)} | Plus: ${String(details.store.planShopifyPlus ?? false)}`],
                      ["Emails", `${details.store.email || "-"} / ${details.store.contactEmail || "-"}`],
                      ["Product count", `${details.store.productCount}`],
                      ["Last sync", formatLocalDateTime(details.store.lastSyncAt)],
                      ["User last seen", formatLocalDateTime(details.store.userLastAccessedAt)],
                      ["Shop updated", formatLocalDateTime(details.store.updatedAt)],
                    ]}
                  />
                  <Checkbox
                    label="Shop is active"
                    checked={details.store.isActive}
                    onChange={handleIsActiveChange}
                    disabled={savingActiveState}
                  />
                </BlockStack>
              ) : null}

              {selectedTab === 1 ? (
                <BlockStack gap="400">
                  <Text as="p" tone="subdued">Auto-refreshes every 10 seconds.</Text>
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">Current bulk operation</Text>
                      {runtime?.currentBulkOperation ? (
                        <DataTable
                          columnContentTypes={["text", "text"]}
                          headings={["Field", "Value"]}
                          rows={[
                            ["ID", runtime.currentBulkOperation.id || "-"],
                            ["Status", runtime.currentBulkOperation.status || "-"],
                            ["Error", runtime.currentBulkOperation.errorCode || "-"],
                            ["Created", formatLocalDateTime(runtime.currentBulkOperation.createdAt)],
                            ["Completed", formatLocalDateTime(runtime.currentBulkOperation.completedAt)],
                            ["Object count", `${runtime.currentBulkOperation.objectCount ?? "-"}`],
                            ["File size", `${runtime.currentBulkOperation.fileSize ?? "-"}`],
                            ["Result URL", runtime.currentBulkOperation.url || "-"],
                            ["Partial data URL", runtime.currentBulkOperation.partialDataUrl || "-"],
                          ]}
                        />
                      ) : (
                        <Text as="p">No bulk operation currently returned by Shopify.</Text>
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="h3" variant="headingMd">Webhook subscriptions</Text>
                        <Button
                          tone="critical"
                          disabled={!runtime || runtime.webhookSubscriptions.length === 0 || deletingAllWebhooks || deletingWebhookId !== null}
                          loading={deletingAllWebhooks}
                          onClick={handleDeleteAllWebhooks}
                        >
                          Delete all
                        </Button>
                      </InlineStack>
                      {!runtime || runtime.webhookSubscriptions.length === 0 ? (
                        <Text as="p">No webhook subscriptions found.</Text>
                      ) : (
                        <DataTable
                          columnContentTypes={["text", "text", "text", "text"]}
                          headings={["Topic", "Endpoint type", "Endpoint", "Action"]}
                          rows={runtime.webhookSubscriptions.map((sub) => [
                            sub.topic,
                            sub.endpointType || "-",
                            endpointText(sub),
                            <Button
                              key={`delete-${sub.id}`}
                              variant="plain"
                              tone="critical"
                              disabled={deletingAllWebhooks || deletingWebhookId === sub.id}
                              onClick={() => handleDeleteWebhook(sub.id)}
                            >
                              Delete
                            </Button>
                          ])}
                        />
                      )}
                    </BlockStack>
                  </Card>
                </BlockStack>
              ) : null}

              {selectedTab === 2 ? (
                <BlockStack gap="300">
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">General store settings</Text>
                      <Checkbox
                        label="Enable persistent filtering on vehicle search"
                        checked={generalSettings.searchEnablePersistentFiltering}
                        onChange={(checked) => handleGeneralSettingChange("searchEnablePersistentFiltering", checked)}
                        disabled={savingGeneralSettings}
                      />
                      <Checkbox
                        label="Use ymm_ prefix for product tags"
                        checked={generalSettings.useYmmPrefixForProductTags}
                        onChange={(checked) => handleGeneralSettingChange("useYmmPrefixForProductTags", checked)}
                        disabled={savingGeneralSettings}
                      />
                      <InlineStack align="end">
                        <Button variant="primary" onClick={handleGeneralSettingsSave} loading={savingGeneralSettings} disabled={savingGeneralSettings}>
                          Save general settings
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  </Card>

                  {sortedSettings.length === 0 ? (
                    <Text as="p">No explicit shop sync settings saved.</Text>
                  ) : (
                    <Card>
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="p" tone="subdued">Edit values and click save per row to update a store sync setting.</Text>
                          <Button
                            tone="critical"
                            onClick={handleClearSurgeMode}
                            loading={clearingSurgeMode}
                            disabled={clearingSurgeMode || savingSettingKey !== null}
                          >
                            Clear surge mode + counts
                          </Button>
                        </InlineStack>
                        <DataTable
                          columnContentTypes={["text", "text", "text"]}
                          headings={["Setting", "Value", "Action"]}
                          rows={sortedSettings.map(([key]) => {
                            const value = editedSettings[key] ?? "";
                            const isSaving = savingSettingKey === key;

                            return [
                              key,
                              <div key={`value-${key}`} style={{ minWidth: "360px" }}>
                                <TextField
                                  label={`Value for ${key}`}
                                  labelHidden
                                  autoComplete="off"
                                  value={value}
                                  onChange={(nextValue) => handleSettingEdit(key, nextValue)}
                                  disabled={isSaving}
                                />
                              </div>,
                              <Button
                                key={`save-${key}`}
                                size="slim"
                                variant="primary"
                                onClick={() => handleSettingSave(key)}
                                loading={isSaving}
                                disabled={isSaving}
                              >
                                Save
                              </Button>
                            ];
                          })}
                        />
                      </BlockStack>
                    </Card>
                  )}
                </BlockStack>
              ) : null}


              {selectedTab === 3 ? (
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">Store notes</Text>
                  <TextField
                    label="Add note"
                    multiline={4}
                    autoComplete="off"
                    value={newNote}
                    onChange={setNewNote}
                    disabled={savingNote}
                  />
                  <InlineStack align="end">
                    <Button
                      variant="primary"
                      onClick={handleAddNote}
                      loading={savingNote}
                      disabled={savingNote || !newNote.trim()}
                    >
                      Add note
                    </Button>
                  </InlineStack>
                  {details.notes.length === 0 ? (
                    <Text as="p">No notes for this store yet.</Text>
                  ) : (
                    <DataTable
                      columnContentTypes={["text", "text", "text"]}
                      headings={["Created", "Created by", "Note"]}
                      rows={details.notes.map((note: AdminStoreNote) => [
                        formatLocalDateTime(note.createdAt),
                        note.createdByAppUserId || "-",
                        note.note,
                      ])}
                    />
                  )}
                </BlockStack>
              ) : null}


              {selectedTab === 4 ? (
                <BlockStack gap="200">
                  <Text as="h3" variant="headingMd">Fitment import history</Text>
                  {details.fitmentImportHistory.length === 0 ? (
                    <Text as="p">No fitment imports found for this store.</Text>
                  ) : (
                    <DataTable
                      columnContentTypes={["text", "text", "text", "text", "text", "text"]}
                      headings={["Queued", "File", "Format", "State", "Completed", "Action"]}
                      rows={details.fitmentImportHistory.map((item) => [
                        formatLocalDateTime(item.addedOn),
                        item.originalFileName || item.storedFileName || `Import ${item.queueId}`,
                        item.importFormat,
                        item.state,
                        formatLocalDateTime(item.completedAt),
                        <InlineStack key={`actions-${item.queueId}`} gap="200">
                          <Button
                            key={`download-${item.queueId}`}
                            size="slim"
                            onClick={() => handleDownloadFitmentImport(item)}
                            loading={downloadingImportQueueId === item.queueId}
                            disabled={downloadingImportQueueId !== null || deletingImportQueueId !== null}
                          >
                            Download
                          </Button>
                          <Button
                            key={`delete-${item.queueId}`}
                            size="slim"
                            tone="critical"
                            onClick={() => handleDeleteFitmentImport(item)}
                            loading={deletingImportQueueId === item.queueId}
                            disabled={deletingImportQueueId !== null || downloadingImportQueueId !== null}
                          >
                            Delete
                          </Button>
                        </InlineStack>
                      ])}
                    />
                  )}
                </BlockStack>
              ) : null}
            </div>
          </Card>
        </>
      )}
    </BlockStack>
  );
}
